// The frame around every page but a session's (byj-1wv.19), after
// brainyard-playground-apps' desktop shell: a sidebar on the left with the
// app's places, which collapses to a rail of icons and is remembered; a top
// bar; and at its right the person's menu — who is signed in, the guide,
// sign out. Below the stacking breakpoint the sidebar is a drawer the top
// bar's menu button opens.
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { User } from "../api";
import { Link } from "./Nav";
import { useKeptFlag } from "../kept";
import { language, offeredLanguages, setLanguage, t, type Language } from "../i18n";
import { AdminIcon, AgentsIcon, BankIcon, BillingIcon, ClassesIcon, DocsIcon, ExamsIcon, FilesIcon, HomeIcon, MachinesIcon, SharesIcon, TemplatesIcon } from "./PlaceIcons";

const CollapsedKey = "share.sidebar.collapsed";

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);
/** The product's mark, beside its name in the sidebar and on the login card (components/CardPage). */
export const Mark = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="6" fill="var(--accent)" />
    <path d="M7 16V8h4.5a2.5 2.5 0 0 1 0 5H7m0 0h5a2.5 2.5 0 0 1 0 5H7" fill="none" stroke="var(--accent-text)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** A place, and the main server's module it is, when it is one (RFC-059 §6). */
const places: { to: string; label: () => string; icon: () => JSX.Element; current: (p: string) => boolean; module?: string }[] = [
  // where a sign-in lands: what Share does, and mine of it (byj-1wv.137)
  { to: "/home", label: () => t("shell:navHome"), icon: HomeIcon, current: (p: string) => p === "/home" },
  // a device's approval is under Shares, whose drawer lists the devices; an invitation too, before
  // it is known what it is for, as most lead to a share, a group or a recording (byj-1wv.51.7)
  { to: "/shares", module: "live", label: () => t("shell:navShares"), icon: SharesIcon, current: (p: string) => p === "/shares" || p.startsWith("/s/") || p.startsWith("/r/") || p.startsWith("/g/") || p === "/device" || p.startsWith("/i/") },
  { to: "/classes", module: "classes", label: () => t("shell:navClasses"), icon: ClassesIcon, current: (p: string) => p === "/classes" || p.startsWith("/c/") },
  { to: "/exams", module: "exams", label: () => t("shell:navExams"), icon: ExamsIcon, current: (p: string) => p === "/exams" || p.startsWith("/e/") || p.startsWith("/ea/") },
  { to: "/templates", module: "app-templates", label: () => t("shell:navTemplates"), icon: TemplatesIcon, current: (p: string) => p === "/templates" || p.startsWith("/t/") },
  { to: "/files", module: "files", label: () => t("shell:navFiles"), icon: FilesIcon, current: (p: string) => p === "/files" || p.startsWith("/f/") },
  { to: "/machines", module: "machines", label: () => t("shell:navMachines"), icon: MachinesIcon, current: (p: string) => p === "/machines" },
  // the agent builder (RFC-060), a main server's module
  { to: "/agents", module: "agents", label: () => t("shell:navAgents"), icon: AgentsIcon, current: (p: string) => p === "/agents" || p.startsWith("/agents/") },
  // a person's plan and its payments (RFC-065), and the plans on sale
  { to: "/billing", module: "billing", label: () => t("shell:navBilling"), icon: BillingIcon, current: (p: string) => p === "/billing" || p === "/pricing" },
  { to: "/docs", label: () => t("shell:navDocs"), icon: DocsIcon, current: (p: string) => p === "/docs" },
];

/** An admin's place, shown to an admin alone (RFC-050 "By grant"). */
const adminPlace = { to: "/admin", label: () => t("shell:navAdmin"), icon: AdminIcon, current: (p: string) => p === "/admin" };

/** The item bank's place, shown to a person let into it (RFC-056). */
const bankPlace = { to: "/bank", label: () => t("shell:navBank"), icon: BankIcon, current: (p: string) => p === "/bank" || p.startsWith("/bank/") };

/**
 * What a third party's people see of Share's places (byj-1wv.117.10): a candidate their exam
 * alone, an operator the console — their classes and exams — and the guide.
 */
const integrationPlaces: Record<NonNullable<User["integration"]>, string[]> = {
  candidate: ["/exams"],
  operator: ["/classes", "/exams", "/docs"],
  // an author's own pages are the exam edition's (RFC-063); without one, their machine
  author: ["/machines", "/docs"],
};

/**
 * The places a person sees: those of the modules the main server serves (all when it says none),
 * and of those a third party's candidate or operator only theirs; one let into the bank sees the
 * Item bank after Exams; an admin Admin, before the guide.
 */
export function placesFor(user: Pick<User, "admin" | "bank" | "modules" | "integration"> | null) {
  const mods = user?.modules;
  const only = user?.integration ? integrationPlaces[user.integration] : null;
  let out = places.filter((p) => (!p.module || !mods || mods.includes(p.module)) && (!only || only.includes(p.to)));
  if (user?.bank && (!mods || mods.includes("bank"))) {
    const at = out.findIndex((p) => p.to === "/exams") + 1;
    out = [...out.slice(0, at), bankPlace, ...out.slice(at)];
  }
  return user?.admin ? [...out.slice(0, -1), adminPlace, out[out.length - 1]!] : out;
}

/** The initials shown as someone's avatar: of their name, or their email's first letter. */
export function initials(user: Pick<User, "name" | "email">): string {
  const words = (user.name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (user.email[0] ?? "?").toUpperCase();
}

/** The language the app is shown in, beside the person's menu; each named in its own words. */
export function LanguageSelect() {
  const offered = offeredLanguages();
  // a profile with one language has nothing to choose
  if (offered.length < 2) return null;
  return (
    <select
      className="small"
      aria-label={t("shell:language")}
      data-testid="language"
      value={language()}
      onChange={(e) => setLanguage(e.target.value as Language)}
    >
      {offered.map((l) => (
        <option key={l.code} value={l.code} lang={l.code}>
          {l.name}
        </option>
      ))}
    </select>
  );
}

/** The top bar's right end: who is signed in, with the guide and sign out under it. */
export function UserMenu({ user, onLogout }: { user: User; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);
  const name = user.name || user.email;
  return (
    <div className="user-menu" ref={box}>
      <button
        type="button"
        className="user-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("login:signedInAs", { name })}
        data-testid="user-menu"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar" aria-hidden="true">
          {initials(user)}
        </span>
        <span className="user-name">{name}</span>
      </button>
      {open && (
        <div className="menu" role="menu" aria-label={t("shell:accountMenu")}>
          <div className="menu-head">
            <strong>{t("login:signedInAs", { name })}</strong>
            {user.name && <span className="muted small">{user.email}</span>}
          </div>
          {/* a third party's candidate is shown the exam alone */}
          {user.integration !== "candidate" && (
            <Link to="/docs" className="menu-item" role="menuitem">
              {t("shell:navDocs")}
            </Link>
          )}
          <button
            type="button"
            className="menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            {t("login:logout")}
          </button>
        </div>
      )}
    </div>
  );
}

export function Shell({
  path,
  user,
  onLogout,
  children,
}: {
  path: string;
  user: User | null;
  onLogout?: () => void;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useKeptFlag(CollapsedKey, false);
  const [drawer, setDrawer] = useState(false);
  const toggle = () => setCollapsed(!collapsed);
  // a navigation closes the drawer
  useEffect(() => setDrawer(false), [path]);
  const mine = placesFor(user);
  const here = mine.find((p) => p.current(path));
  return (
    <div className={"shell" + (collapsed ? " shell-collapsed" : "") + (drawer ? " shell-drawer" : "")}>
      <nav className="sidebar" aria-label={t("shell:navMain")} data-testid="sidebar">
        <div className="sb-brand">
          <Mark />
          <span className="sb-label">{t("common:appName")}</span>
        </div>
        <ul className="sb-places">
          {mine.map((p) => (
            <li key={p.to}>
              <Link to={p.to} className="sb-link" current={p.current(path)}>
                <p.icon />
                <span className="sb-label">{p.label()}</span>
                {collapsed && <span className="sb-tip">{p.label()}</span>}
              </Link>
            </li>
          ))}
        </ul>
        <button type="button" className="sb-toggle" onClick={toggle} aria-expanded={!collapsed} title={collapsed ? t("shell:sidebarExpand") : t("shell:sidebarCollapse")}>
          <span aria-hidden="true">{collapsed ? "›" : "‹"}</span>
          <span className="sb-label">{t("shell:sidebarCollapse")}</span>
        </button>
      </nav>
      {drawer && <div className="shell-scrim" onClick={() => setDrawer(false)} />}
      <div className="shell-main">
        <header className="topbar">
          <button type="button" className="tb-menu" onClick={() => setDrawer((d) => !d)} aria-label={t("shell:navMain")} aria-expanded={drawer}>
            <MenuIcon />
          </button>
          <span className="tb-here">{here?.label() ?? ""}</span>
          <span className="tb-spacer" />
          <LanguageSelect />
          {user && onLogout ? (
            <UserMenu user={user} onLogout={onLogout} />
          ) : (
            <Link to="/login" className="button small">
              {t("shell:signIn")}
            </Link>
          )}
        </header>
        {children}
      </div>
    </div>
  );
}
